import { CreditCardFilled } from '@ant-design/icons';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import { getRouteStackPath } from '@portal/config/routes';
import useReduxState from '@portal/hooks/useReduxState';
import { Card } from '@portal/models/bank';
import {
  CardStatus,
  CardStatusDock,
  CardType,
} from '@portal/models/enumerators/cards';
import { translate } from '@portal/services/i18n';
import { getEnumDescription } from '@portal/utils/enum';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import './MyCards.less';

import { CardActions } from '@portal/actions/index';
import CardFrontPJ from '@portal/assets/img/card-front-pj.png';
import CardFront from '@portal/assets/img/card_front.png';
import GenericModal from '@portal/components/GenericModal/GenericModal';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import RolesType from '@portal/enum/rolesType';
import { useNavigation } from '@portal/hooks/useNavigation';
import { AccountTaxIdentifierType } from '@portal/models/enumerators';
import { hasAccess } from '@portal/utils/hasAccess';
import { Spin } from 'antd';

const CARD_NUMBER_LAST_DIGITS = 4;
const CARD_NUMBER_MASK_CHAR = '*';

const BLOCKED_CARD_STATUSES: ReadonlyArray<CardStatusDock> = [
  CardStatusDock.BLOCKED,
  CardStatusDock.MIGRATED_BLOCKED,
  CardStatusDock.BLOCKED_PREVENTIVE,
];

const formatCardNumber = (cardNumber?: string) => {
  if (!cardNumber) return '';
  return cardNumber
    .slice(-CARD_NUMBER_LAST_DIGITS)
    .padStart(CARD_NUMBER_LAST_DIGITS, CARD_NUMBER_MASK_CHAR);
};

const getStatusClassName = (status?: CardStatusDock | CardStatus) => {
  const base = 'my-cards__grid__item__format';
  if (status === CardStatusDock.NORMAL) return `${base} ${base}--active`;
  if (BLOCKED_CARD_STATUSES.includes(status as CardStatusDock)) {
    return `${base} ${base}--blocked`;
  }
  return base;
};

const MyCards = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const {
    theme: { currentTheme },
    user: { me, roles },
    cards: { cards },
  } = useReduxState();

  const [functionalityUnavailable, setFunctionalityUnavailable] =
    useState(false);
  const [hasAccessToCardDetails, setHasAccessToCardDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCardImageLoaded, setIsCardImageLoaded] = useState(false);

  const isPF = me?.account?.type === AccountTaxIdentifierType.PERSON;
  const imageFront = isPF ? `url(${CardFront})` : `url(${CardFrontPJ})`;
  const imageSrc = isPF ? CardFront : CardFrontPJ;

  const hasCardDetailsAccess = useMemo(
    () => hasAccess(roles, RolesType.CARD_DETAILS_READ),
    [roles]
  );

  const hasMyAccountAccess = useMemo(
    () => hasAccess(roles, RolesType.MY_ACCOUNT),
    [roles]
  );

  useEffect(() => {
    if (!me?.id) return;

    setIsLoading(true);
    setFunctionalityUnavailable(false);

    dispatch(
      CardActions.getAllCards(me?.id ?? '', cards.length === 0, (err) => {
        if (err) {
          setFunctionalityUnavailable(true);
        }
        setIsLoading(false);
      })
    );
  }, [dispatch, me?.id]);

  const handleViewDetails = useCallback(
    (cardId: string) => {
      if (!cardId) return;

      if (!hasCardDetailsAccess) {
        setHasAccessToCardDetails(true);
        return;
      }

      navigate(
        getRouteStackPath('CARDS', 'MY_CARD_DETAILS', {
          id: cardId,
        })
      );
    },
    [hasCardDetailsAccess, navigate]
  );

  const handleCardClick = useCallback(
    (cardId: string, event: React.MouseEvent | React.KeyboardEvent) => {
      if (
        event.type === 'keydown' &&
        (event as React.KeyboardEvent).key !== 'Enter' &&
        (event as React.KeyboardEvent).key !== ' '
      ) {
        return;
      }
      event.preventDefault();
      handleViewDetails(cardId);
    },
    [handleViewDetails]
  );

  const renderCards = useCallback(
    (title: string, description: string, cards: Card[]) => {
      const hasPhysicalCard = cards.some(
        ({ type }) => type === CardType.PHYSICAL
      );

      const hasVirtualCard = cards.some(
        ({ type }) => type === CardType.VIRTUAL
      );

      const shouldShowRequestPhysical = !hasPhysicalCard && hasVirtualCard;
      const shouldShowRequestVirtual = hasPhysicalCard && !hasVirtualCard;

      const handleRequestCard = () =>
        navigate(getRouteStackPath('CARDS', 'REQUEST_CARD'));

      const requestButtonText = shouldShowRequestPhysical
        ? translate('PAGES.CARDS.MY_CARDS.BUTTONS.REQUEST_CARD_PHYSICAL')
        : shouldShowRequestVirtual
        ? translate('PAGES.CARDS.MY_CARDS.BUTTONS.REQUEST_CARD_VIRTUAL')
        : null;

      return (
        <AdvancedCard testId="my-cards-card">
          <div className="my-cards__header">
            <div>
              <ThemedText variant="h3">{title}</ThemedText>
              <ThemedText>{description}</ThemedText>
            </div>
            {requestButtonText ? (
              <div className="my-cards__buttons">
                <AdvancedButton
                  text={requestButtonText}
                  onClick={handleRequestCard}
                  disabled={!hasMyAccountAccess}
                />
              </div>
            ) : null}
          </div>

          {cards.map((card) => {
            const isInteractive = Boolean(card.id) && hasCardDetailsAccess;

            return (
              <div className="my-cards__card-row" key={card.id}>
                <div
                  className={`my-cards__card-row__image${
                    isCardImageLoaded
                      ? ''
                      : ' my-cards__card-row__image--skeleton'
                  }`}
                  style={{
                    backgroundImage: isCardImageLoaded ? imageFront : undefined,
                    cursor: isCardImageLoaded
                      ? isInteractive
                        ? 'pointer'
                        : 'not-allowed'
                      : 'default',
                    opacity: isCardImageLoaded && !isInteractive ? 0.5 : 1,
                  }}
                  onClick={
                    isInteractive && isCardImageLoaded
                      ? (e) => handleCardClick(card.id ?? '', e)
                      : undefined
                  }
                  onKeyDown={
                    isInteractive && isCardImageLoaded
                      ? (e) => handleCardClick(card.id ?? '', e)
                      : undefined
                  }
                  role={isInteractive ? 'button' : undefined}
                  tabIndex={isInteractive ? 0 : -1}
                  aria-disabled={!isInteractive}
                  aria-label={translate('PAGES.CARDS.MY_CARDS.BUTTONS.DETAILS')}
                >
                  {!isCardImageLoaded && (
                    <img
                      src={imageSrc}
                      alt=""
                      style={{ display: 'none' }}
                      onLoad={() => setIsCardImageLoaded(true)}
                      onError={() => setIsCardImageLoaded(true)}
                    />
                  )}
                </div>
                <div className="my-cards__card-row__info">
                  <ThemedText variant="body">
                    {getEnumDescription(card.type, 'CARD_TYPE_NUMBER')}
                  </ThemedText>
                  <div className="my-cards__card-row__info__pills">
                    <div className="my-cards__grid__item__format">
                      <ThemedText variant="body">
                        {translate('PAGES.CARDS.MY_CARDS.BUTTONS.FINAL')}{' '}
                        {formatCardNumber(card.cardNumber)}
                      </ThemedText>
                    </div>
                    <div className={getStatusClassName(card.status)}>
                      <ThemedText variant="body">
                        {getEnumDescription(
                          card.status ?? '',
                          'CARD_STATUS_DOCK'
                        )}
                      </ThemedText>
                    </div>
                  </div>
                  <AdvancedButton
                    variant="outlined"
                    text={translate('PAGES.CARDS.MY_CARDS.BUTTONS.DETAILS')}
                    disabled={!card.id}
                    onClick={() => handleViewDetails(card.id ?? '')}
                  />
                </div>
              </div>
            );
          })}
        </AdvancedCard>
      );
    },
    [
      imageFront,
      imageSrc,
      isCardImageLoaded,
      setIsCardImageLoaded,
      hasMyAccountAccess,
      hasCardDetailsAccess,
      handleCardClick,
      handleViewDetails,
    ]
  );

  const handleAction = useCallback(() => {
    if (functionalityUnavailable) {
      navigate(getRouteStackPath('HOME', 'HOME_PAGE'));
      return;
    }

    navigate(getRouteStackPath('CARDS', 'REQUEST_CARD'));
  }, [functionalityUnavailable, navigate]);

  const renderFunctionalityUnavailable = useMemo(
    () => (
      <AdvancedCard testId="my-cards-functionality-unavailable-card">
        <div className="my-cards__empty">
          <div
            className="my-cards__empty__icon"
            style={{ background: currentTheme.background }}
          >
            <CreditCardFilled />
          </div>
          <ThemedText variant="body" className="my-cards__empty__title">
            {translate('PAGES.CARDS.MY_CARDS.FUNCTIONALITY_UNAVAILABLE.TITLE')}
          </ThemedText>
          <ThemedText variant="body" className="my-cards__empty__text">
            {translate(
              'PAGES.CARDS.MY_CARDS.FUNCTIONALITY_UNAVAILABLE.DESCRIPTION'
            )}
          </ThemedText>
          <div className="my-cards__empty__actions">
            <AdvancedButton
              text={translate(
                'PAGES.CARDS.MY_CARDS.FUNCTIONALITY_UNAVAILABLE.BUTTON'
              )}
              variant="contained"
              onClick={handleAction}
            />
          </div>
        </div>
      </AdvancedCard>
    ),
    [currentTheme.background, handleAction]
  );

  const renderEmptyState = useMemo(
    () => (
      <AdvancedCard testId="my-cards-empty-state-card">
        <div className="my-cards__empty">
          <div
            className="my-cards__empty__icon"
            style={{ background: currentTheme.background }}
          >
            <CreditCardFilled />
          </div>
          <ThemedText variant="body" className="my-cards__empty__title">
            {translate('PAGES.CARDS.MY_CARDS.EMPTY_STATE.TITLE')}
          </ThemedText>
          <ThemedText variant="body" className="my-cards__empty__text">
            {translate('PAGES.CARDS.MY_CARDS.EMPTY_STATE.DESCRIPTION')}
          </ThemedText>
          <div className="my-cards__empty__actions">
            <AdvancedButton
              text={translate('PAGES.CARDS.MY_CARDS.EMPTY_STATE.BUTTON')}
              variant="contained"
              onClick={handleAction}
            />
          </div>
        </div>
      </AdvancedCard>
    ),
    [currentTheme.background, handleAction]
  );

  const renderLoadingState = useMemo(
    () => (
      <AdvancedCard testId="my-cards-loading-card">
        <div className="my-cards__empty">
          <Spin size="large" />
          <ThemedText variant="body">
            {translate('PAGES.CARDS.MY_CARDS.LOADING')}
          </ThemedText>
        </div>
      </AdvancedCard>
    ),
    []
  );

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: false,
            title: translate('COMPONENTS.BREADCRUMB.CARDS'),
            url: getRouteStackPath('CARDS', 'CARDS_PAGE'),
          },
        ]}
      />
      <div className="my-cards">
        <AdvancedPanelContentHeader
          testId="my-cards-header-panel"
          title={translate('PAGES.CARDS.MY_CARDS.TITLE')}
          description={translate('PAGES.CARDS.MY_CARDS.TEXT')}
          goBackAction={() => navigate(getRouteStackPath('HOME', 'HOME_PAGE'))}
        />
        {isLoading ? (
          renderLoadingState
        ) : functionalityUnavailable ? (
          renderFunctionalityUnavailable
        ) : cards.length === 0 ? (
          renderEmptyState
        ) : (
          <>
            {renderCards(
              translate('PAGES.CARDS.MY_CARDS.DETAILS.DEBIT_CARD.TITLE'),
              translate('PAGES.CARDS.MY_CARDS.DETAILS.DEBIT_CARD.TEXT'),
              cards
            )}
          </>
        )}
      </div>
      <GenericModal
        visible={hasAccessToCardDetails}
        onClose={() => setHasAccessToCardDetails(false)}
        title={translate('PAGES.CARDS.MY_CARDS.MODAL_PERMISSION.TITLE')}
        description={translate(
          'PAGES.CARDS.MY_CARDS.MODAL_PERMISSION.DESCRIPTION'
        )}
      />
    </>
  );
};

export default MyCards;
